// apps/web/src/render/print-root.tsx
//
// Phase 11.8 — REND-01 (Strand 3) the #print-root component. Adopts the
// reference's separate print-root architecture: ONE hidden DOM tree mounted ONCE
// in RecipeRenderEngine's Fragment (inside the caller-supplied provider, a
// top-level sibling of the live tree). On screen it is display:none (globals.css);
// under @media print the live tree is suppressed and this becomes the visible
// document. It is rebuilt per render to reflect the active mode + current
// selections.
//
// DISPLAY-ONLY: the print-root OMITS all interactive controls (no Reset-views, no
// Clear-checkmarks, no collapse toggles, no print-mode-switcher on the printed
// pages). The per-line print checkbox is an UNCONDITIONAL EMPTY BOX — it NEVER
// inherits any on-screen check state (a fresh hand-tick checklist for the cook).
//
// It reads the SAME recipe + the SAME widget-state context as the live tree (V4
// — no new data-access path). It resolves the active mode from the spread-local
// printOptions (a generic string-key read; default the FIRST printModes[] entry;
// drift → the first entry). Per-layout emission:
//   - grouped-by-category → a master list grouped by the recipe-declared buckets,
//     REUSING the Plan-04 aggregate helper (display-only re-emission of the same
//     grouped data), amounts via the Plan-03 formatter.
//   - flight-style → one card per selected option.
//   - sequential (schema-valid, not in the fixture) → a generic flow; no crash.
//
// SCOPE FENCE: STRUCTURAL + FUNCTIONAL parity only — render at NATURAL size. The
// pixel / PDF / page.pdf() polish is REND-10 → Phase 15 and is OUT of scope.
//
// C1 generality (scripts/grep-no-recipe-identifiers.sh): the print-root names NO
// unit, NO category, NO mode/flavor literal. Mode labels + bucket labels + names
// + amounts ALL flow from recipe DATA / printModes[]. Bucket labels via the
// REUSED resolveGroupLabel; amounts via the units-opaque formatAmount.
//
// XSS discipline (T-11.8-XSS): every DATA string is a JSX text child (React
// auto-escapes). No dangerouslySetInnerHTML.
import type {
  MeasurementVocabulary,
  PrintMode,
  RecipeDefinitionV1,
} from "@the-recipe-spread/shared";
import { useContext } from "react";
import { resolveSizeIndex } from "@/widgets/branch-variant/format-override-value";
import { formatAmount } from "@/widgets/measurement/format-amount";
import { RecipeStateContext } from "@/widgets/recipe-state-provider";
import { type AggregateGroupInput, aggregate } from "@/widgets/shopping-list-aggregator/aggregate";
import { resolveGroupLabel } from "@/widgets/summary-checklist/group-label";

const FLAT_BUCKET_KEY = "_flat";
const EMDASH = "—";

export interface PrintRootProps {
  recipe: RecipeDefinitionV1;
  // The active print-mode id (lifted into the engine). Absent / drifted → the
  // first printModes[] entry. The mode itself is resolved from recipe.printModes.
  activeModeId?: string | undefined;
}

// Render one amount as a plain string via the units-opaque formatter (print joins
// the alternates with "; " inside one parenthetical). Undefined → the U+2014 mark.
function formatPrintAmount(
  amount: number | undefined,
  vocab: MeasurementVocabulary | undefined,
): string {
  if (amount === undefined || vocab === undefined) return EMDASH;
  const formatted = formatAmount(amount, vocab);
  if (formatted === undefined) return EMDASH;
  if (formatted.alts.length === 0) return formatted.primary;
  return `${formatted.primary} (${formatted.alts.join("; ")})`;
}

// Read the selection + sizing inputs from the widget-state context generically:
// the FIRST state shaped like an option-set {selected} drives pass selection; the
// FIRST state shaped like a scaling-matrix {axisSelections, multiplier} (paired
// with its widget's first axis) drives the per-pass scale. No recipe literal.
interface ResolvedInputs {
  readonly selectedCount: number;
  readonly selectedIds: ReadonlyArray<string>;
  readonly scale: number;
}

function resolveInputs(
  recipe: RecipeDefinitionV1,
  states: ReadonlyMap<string, unknown>,
): ResolvedInputs {
  let selectedIds: ReadonlyArray<string> = [];
  let scale = 1;
  for (const [id, state] of states) {
    if (
      selectedIds.length === 0 &&
      typeof state === "object" &&
      state !== null &&
      "selected" in state &&
      Array.isArray((state as { selected: unknown }).selected)
    ) {
      selectedIds = Array.from(new Set((state as { selected: ReadonlyArray<string> }).selected));
    }
    if (
      typeof state === "object" &&
      state !== null &&
      "axisSelections" in state &&
      "multiplier" in state
    ) {
      scale = resolveScaleFor(recipe, id, state as ScalerLike);
    }
  }
  return { selectedCount: selectedIds.length, selectedIds, scale };
}

interface ScalerLike {
  readonly axisSelections: Readonly<Record<string, string>>;
  readonly multiplier: number;
}

function resolveScaleFor(recipe: RecipeDefinitionV1, widgetId: string, state: ScalerLike): number {
  const widget = (recipe.widgets ?? []).find((w) => w.instanceId === widgetId);
  const axes = (
    widget?.config as
      | {
          axes?: ReadonlyArray<{
            id: string;
            steps: ReadonlyArray<{ id: string; multiplier: number }>;
          }>;
        }
      | undefined
  )?.axes;
  const sizeAxis = axes?.[0];
  const typed = state.multiplier;
  if (sizeAxis === undefined) {
    return Number.isFinite(typed) && typed > 0 ? typed : 1;
  }
  const idx = resolveSizeIndex(sizeAxis.steps, state.axisSelections[sizeAxis.id]);
  const stepMul = idx >= 0 ? sizeAxis.steps[idx]?.multiplier : undefined;
  if (stepMul === undefined || !Number.isFinite(stepMul) || !Number.isFinite(typed)) {
    return Number.isFinite(typed) && typed > 0 ? typed : 1;
  }
  const product = stepMul * typed;
  return Number.isFinite(product) && product > 0 ? product : 1;
}

// An unconditional empty print checkbox — a static box, NEVER bound to any
// on-screen check/printIncluded state (the fresh-hand-tick-checklist contract).
function PrintCheckbox(): React.ReactElement {
  return (
    <span
      aria-hidden
      className="mr-2 inline-block h-3 w-3 shrink-0 border border-[var(--recipe-ink)]"
    />
  );
}

// The grouped-by-category emission — REUSES the Plan-04 aggregate helper, then
// renders display-only (no controls). Each line carries the unconditional empty
// checkbox.
function GroupedEmission({
  recipe,
  inputs,
}: {
  recipe: RecipeDefinitionV1;
  inputs: ResolvedInputs;
}): React.ReactElement {
  const vocab = recipe.measurementVocabulary;
  const groups: ReadonlyArray<AggregateGroupInput> = (recipe.ingredientGroups ?? []).map((g) => ({
    bucketKey: g.label,
    lines: g.items.map((item) => ({ name: item.name, amount: item.quantity, note: item.note })),
  }));
  const buckets = aggregate({
    groups,
    passCount: Math.max(1, inputs.selectedCount),
    scale: inputs.scale,
    flat: false,
    flatBucketKey: FLAT_BUCKET_KEY,
  });
  return (
    <div className="flex flex-col gap-3">
      {buckets.map((bucket) => {
        const label = resolveGroupLabel(bucket.bucketKey, recipe.optionCategories);
        return (
          <section key={bucket.bucketKey} className="flex flex-col gap-1">
            {label !== "" ? <h3 className="text-sm font-semibold">{label}</h3> : null}
            <ul className="flex flex-col gap-1">
              {bucket.lines.map((line) => (
                <li key={line.name} className="flex items-baseline text-sm">
                  <PrintCheckbox />
                  <span className="mr-1">{formatPrintAmount(line.amount, vocab)}</span>
                  <span>{line.name}</span>
                  {line.note !== undefined ? (
                    <span className="ml-1 text-xs text-[var(--recipe-muted)]">{line.note}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

// The flight-style emission — one card per selected option (display-only). The
// option labels come from the FIRST option-set widget's config.options DATA.
function FlightEmission({
  recipe,
  inputs,
}: {
  recipe: RecipeDefinitionV1;
  inputs: ResolvedInputs;
}): React.ReactElement {
  // Resolve option labels generically from the first option-set widget.
  const optionSet = (recipe.widgets ?? []).find((w) => w.templateId === "option-set");
  const options =
    (optionSet?.config as { options?: ReadonlyArray<{ id: string; label: string }> } | undefined)
      ?.options ?? [];
  const labelFor = (id: string): string => options.find((o) => o.id === id)?.label ?? id;
  if (inputs.selectedIds.length === 0) {
    return <p className="text-sm text-[var(--recipe-muted)]">Nothing selected.</p>;
  }
  return (
    <div className="flex flex-col gap-3">
      {inputs.selectedIds.map((id) => (
        <section key={id} className="border border-[var(--recipe-line)] p-3">
          <h3 className="text-sm font-semibold">{labelFor(id)}</h3>
        </section>
      ))}
    </div>
  );
}

// The sequential emission — a generic flat flow in declaration order (the
// schema-valid-but-unfixtured layout). Renders the grouped data as one flat list;
// MUST NOT crash.
function SequentialEmission({
  recipe,
  inputs,
}: {
  recipe: RecipeDefinitionV1;
  inputs: ResolvedInputs;
}): React.ReactElement {
  const vocab = recipe.measurementVocabulary;
  const groups: ReadonlyArray<AggregateGroupInput> = (recipe.ingredientGroups ?? []).map((g) => ({
    bucketKey: g.label,
    lines: g.items.map((item) => ({ name: item.name, amount: item.quantity, note: item.note })),
  }));
  const buckets = aggregate({
    groups,
    passCount: Math.max(1, inputs.selectedCount),
    scale: inputs.scale,
    flat: true,
    flatBucketKey: FLAT_BUCKET_KEY,
  });
  const lines = buckets[0]?.lines ?? [];
  return (
    <ul className="flex flex-col gap-1">
      {lines.map((line) => (
        <li key={line.name} className="flex items-baseline text-sm">
          <PrintCheckbox />
          <span className="mr-1">{formatPrintAmount(line.amount, vocab)}</span>
          <span>{line.name}</span>
        </li>
      ))}
    </ul>
  );
}

export function PrintRoot({ recipe, activeModeId }: PrintRootProps): React.ReactElement | null {
  const ctx = useContext(RecipeStateContext);

  const modes = recipe.printModes ?? [];
  // Gate on printModes presence — no modes → no print-root content.
  if (modes.length === 0) return null;

  // Resolve the active mode: the id prop → a matching mode; absent / drifted →
  // the FIRST mode (the reference default). modes[0] exists (length > 0 above).
  const firstMode = modes[0] as PrintMode;
  const activeMode = modes.find((m) => m.id === activeModeId) ?? firstMode;

  // Read the SAME widget-state context as the live tree (no new data path).
  const states: ReadonlyMap<string, unknown> = ctx === null ? new Map() : ctx.states;
  const inputs = resolveInputs(recipe, states);

  let body: React.ReactElement;
  if (activeMode.layout === "flight-style") {
    body = <FlightEmission recipe={recipe} inputs={inputs} />;
  } else if (activeMode.layout === "sequential") {
    body = <SequentialEmission recipe={recipe} inputs={inputs} />;
  } else {
    // grouped-by-category (default) — also the fall-through for any value the
    // renderer does not special-case.
    body = <GroupedEmission recipe={recipe} inputs={inputs} />;
  }

  return (
    <div id="print-root">
      <h2 className="text-base font-semibold">{recipe.title}</h2>
      {body}
    </div>
  );
}
PrintRoot.displayName = "PrintRoot";
